import { JSXElementConstructor, ReactElement, ReactNode, ReactPortal, useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, DotProps, ErrorBar, Legend, Line, LineChart, RectangleProps, ResponsiveContainer, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import queryType from "../QueryType";
import { Checkbox, CircularProgress } from "@mui/material";
import '../index.css';
import { initializeApp } from 'firebase/app';
import { doc, getDoc, getFirestore } from "firebase/firestore";

const tealColor = '#3BBA9C'
const tealColorClear = '#3BBA9CAA'
const offWhiteColor = '#EEEEEE'
let individualTeamsToggle = false
let individualTeamsVisible : string[] = []

function getMean(data : number[]) {
    return data.reduce((previousValue, currentValue) => previousValue + currentValue, 0) / data.length
}

function getMedian(data: number[]) {
    let median : number | undefined;
    if (data.length % 2 == 1) {
        median = data.at(Math.floor(data.length / 2))
    }
    else {
        median = (data.at(data.length / 2)! + data.at(data.length / 2 - 1)!) / 2
    }
    return median
}

function getLowerQuartile(data: number[]) {
    let dataLength : number = data.length
    if (dataLength % 4 == 0) {
        return (data[dataLength / 4] + data[dataLength / 4 - 1]) / 2
    }
    else {
        return data[Math.floor(dataLength / 4)]
    }
}

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getFirestore(app);

function getUpperQuartile(data: number[]) {
    let dataLength : number = data.length
    if (dataLength % 4 == 0) {
        return (data[dataLength - (dataLength / 4)] + data[dataLength - (dataLength / 4) - 1]) / 2
    }
    else {
        return data[dataLength - Math.floor(dataLength / 4) - 1]
    }
}

function GraphOutputComponent(props: queryType) {
    type graphDataType = [{}?]
    const [newData, setNewData] = useState([] as graphDataType)
    const [show, setShow] = useState(true)
    const [graphInvalidData, setGraphInvalidData] = useState([] as string[])

    async function fetchData() {
        let startYear : number = props.startYear as number
        let endYear : number = props.endYear as number
        if (startYear < 1997 || startYear > 2026 || endYear < 1997 || endYear > 2026 || startYear > endYear) {
            return;
        }
        // Include the year prior to the start year so we can gather "yearsPlayed" data for the prior year
        // If the start year is 2022, we need to add another year to the span since 2021 is never looped over
        const yearAdditionFor2022 = startYear == 2022 ? 1 : 0
        const years : number[] = Array.from({length: endYear - startYear + 2 + yearAdditionFor2022}, (_, index) => startYear - (1 + yearAdditionFor2022) + index)
        const allPercentileRanks : any = {};
        // Retrieve team data and validate teams
        let invalidTeams : string[] = [];
        let teamData : any = [];
        let yearsPlayed : any = {};
        for (let index in props.teams) {
            const team : string = props.teams[index as any].team
            const teamRef = doc(database, "teams", "frc" + team)
            const docSnap = await getDoc(teamRef)
            if (docSnap.exists()) {
                teamData[team] = docSnap.data()
            }
            else {
                invalidTeams.push(team)
            }
        }
        if (invalidTeams.length > 0) {
            setGraphInvalidData(invalidTeams);
            return
        }
        // For each year in the range
        for (let yearIndex in years) {
            const year : number = years[yearIndex]
            // If the current year is 2022, the last season is 2020. Otherwise, it is the prior year
            const lastYear : number = year == 2022 ? 2019 : year-1
            // Remove 2021 from the graph, because of the Covid cancellation
            if (year != 2021 && year != 2020) {
                // Get the percentile ranking for each team in their own district
                const percentileRanksTeamPairs : any = {};
                let returningVeterans = 0;
                let restartedVeterans = 0;
                let rookieTeams = 0;
                let foldedTeams = 0;
                let totalTeamsPlayed = 0;
                let eventsWon = 0;
                let awardsWon = 0;
                let dcmpSlots = 0;
                for (let index in props.teams) {
                    const team = props.teams![index as any].team
                    const teamStartYear = props.teams![index as any].startYear
                    const teamEndYear = props.teams![index as any].endYear
                    // If the year is not in the range for this particular team, skip
                    if (teamEndYear != undefined && teamStartYear != undefined
                        && (year > teamEndYear! || year < teamStartYear!)) {
                        continue
                    }
                    const currentTeamData : { [index: string]: any; } = teamData[team]
                    let currentTeamYearsPlayed : string[] = Object.keys(currentTeamData)
                    const currentTeamCurrentYearData = currentTeamData[year]
                    if (currentTeamCurrentYearData === undefined) {
                        if (currentTeamYearsPlayed.includes(String(lastYear))) {
                            foldedTeams += 1
                        }
                        continue
                    }
                    const currentTeamCurrentYearPoints : number = currentTeamCurrentYearData["district_points"] as number
                    const currentTeamCurrentYearPercentile : number = currentTeamCurrentYearData["percentile"] as number
                    if (currentTeamCurrentYearPoints != 0) {
                        // Did the team win any events or awards?
                        // Aggregate data
                        eventsWon += currentTeamData[year]["events_won"] || 0;
                        awardsWon += currentTeamData[year]["awards_won"] || 0;
                        dcmpSlots += currentTeamData[year]["dcmp_qualified"]! ? 1 : 0;
                        percentileRanksTeamPairs[team] = currentTeamCurrentYearPercentile
                        totalTeamsPlayed += 1
                        // If the team also played the prior year
                        if (currentTeamYearsPlayed.includes(String(lastYear)) && currentTeamData[lastYear]["district_points"] != 0) {
                            returningVeterans += 1;
                        }
                        // If there are no prior years of play
                        else if (Array.from({length: (lastYear) - 1992 + 1}, (_, index) => 1992 + index).some((year) => currentTeamYearsPlayed.includes(String(year))) == false) {
                            rookieTeams += 1
                        }
                        // If the team didn't play in the prior year, but played in at least one year before
                        else {
                            restartedVeterans += 1;
                        }
                    }
                    else if (currentTeamYearsPlayed.includes(String(lastYear))) {
                        foldedTeams += 1
                    }
                }
                const percentileRanks : number[] = Object.values(percentileRanksTeamPairs)
                // Sort the data
                percentileRanks.sort(function(a, b) {
                    return a - b;
                })
                // Get the mean
                let mean : number = getMean(percentileRanks)
                // Get the median
                let median : number | undefined = getMedian(percentileRanks);
                // Get the lower quartile
                let lowerQuartile : number = getLowerQuartile(percentileRanks);
                // Get the upper quartile
                let upperQuartile : number = getUpperQuartile(percentileRanks);
                // Number of teams
                let numTeams : number = totalTeamsPlayed;
                // If no teams participated, 
                if (numTeams == 0 && year != startYear - 1 - yearAdditionFor2022) {
                    newData.push({
                        Year: year,
                        numTeams: numTeams,
                        returningVeterans: returningVeterans,
                        restartedVeterans: restartedVeterans,
                        rookieTeams: rookieTeams,
                        foldedTeams: foldedTeams,
                        eventsWon: eventsWon,
                        awardsWon: awardsWon,
                        dcmpSlots: dcmpSlots
                    })
                }
                // Since we loop through the year prior to the inputted start year for the purpose of populating the "yearsPlayed" data,
                // We have to ensure that we don't display that year's data
                else if (year != startYear - 1 - yearAdditionFor2022) {
                    const thisYearData : any = {
                        Year: year,
                        median: Number(median!.toFixed(2)),
                        mean: Number(mean.toFixed(2)),
                        min: percentileRanks[0],
                        bottomWhisker: lowerQuartile - percentileRanks[0],
                        bottomBox: median! - lowerQuartile, 
                        topBox: upperQuartile - median!,
                        topWhisker: percentileRanks[percentileRanks.length - 1] - upperQuartile, 
                        max: percentileRanks[percentileRanks.length - 1],
                        numTeams: numTeams,
                        returningVeterans: returningVeterans,
                        restartedVeterans: restartedVeterans,
                        rookieTeams: rookieTeams,
                        foldedTeams: foldedTeams,
                        eventsWon: eventsWon,
                        awardsWon: awardsWon,
                        dcmpSlots: dcmpSlots,
                    }
                    for (const index in props.teams) {
                        const key : string = props.teams[index as any].team as string
                        thisYearData[key] = percentileRanksTeamPairs[key]
                    }
                    newData.push(thisYearData)
                }
                allPercentileRanks[year] = percentileRanks
                setNewData(newData)
            }
        }
        if (typeof newData.at(0) == "object") {
            newData.slice(0, 1)
            setNewData(newData)
        }
        setShow(false);
    }

    fetchData()
    
    const BarOutline = (props: RectangleProps) => {
        const { x, y, width, height } = props;

        if (x == null || y == null || width == null || height == null) {
            return null;
        }
        
        return (
            <rect
              x={x+1*width/3}
              y={y}
              x1={x+width}
              y1={y+height}
              width={width / 3}
              height={height}
              stroke={tealColor}
              strokeWidth={2}
              opacity={"50%"}
              fillOpacity={"0%"}
            >
            </rect>
        );
    };

    const DotBar = (props: RectangleProps) => {
        const { x, y, width, height } = props;
      
        if (x == null || y == null || width == null || height == null) {
          return null;
        }
      
        return (
          <line
            x1={x + width / 2}
            y1={y}
            x2={x + width / 2}
            y2={y + height}
            stroke={tealColor}
            strokeWidth={2}
            opacity={"50%"}
            fillOpacity={"0%"}
          />
        );
      };
      
    const HorizonBar = (props: RectangleProps) => {
        const { x, y, width, height } = props;
      
        if (x == null || y == null || width == null || height == null) {
          return null;
        }
      
        return <line 
            x1={x + 1 * width / 3} 
            y1={y} 
            x2={x + 2 *width / 3} 
            y2={y + height} 
            stroke={tealColor}
            strokeWidth={2}
            opacity={"50%"}
            fillOpacity={"0%"} 
        />;
    };

    const CustomTooltip = ({ active, payload, label } : any) => {
        if (active && Array.isArray(payload) && payload.length > 0) {
            const valuesByKey = payload.reduce((acc: Record<string, number>, entry: any) => {
                if (entry && typeof entry === 'object' && entry.dataKey) {
                    acc[entry.dataKey] = Number(entry.value ?? 0)
                }
                return acc
            }, {})

            const numTeams = Number(valuesByKey.numTeams ?? 0)
            const mean = numTeams === 0 ? 0 : Number(valuesByKey.mean ?? 0)
            const median = numTeams === 0 ? 0 : Number(valuesByKey.median ?? 0)
            const min = numTeams === 0 ? 0 : Number(valuesByKey.min ?? 0)
            const bottomWhiskerBarHeight = numTeams === 0 ? 0 : Number(valuesByKey.bottomWhisker ?? 0)
            const lowerQuartile = numTeams === 0 ? 0 : min + bottomWhiskerBarHeight
            const topWhiskerBarHeight = numTeams === 0 ? 0 : Number(valuesByKey.topWhisker ?? 0)
            const upperQuartile = numTeams === 0 ? 0 : Number(valuesByKey.median ?? 0) + topWhiskerBarHeight
            const max = numTeams === 0 ? 0 : Number(valuesByKey.max ?? 0)
            const returningVeterans = Number(valuesByKey.returningVeterans ?? 0)
            const restartedVeterans = Number(valuesByKey.restartedVeterans ?? 0)
            const rookieTeams = Number(valuesByKey.rookieTeams ?? 0)
            const foldedTeams = Number(valuesByKey.foldedTeams ?? 0)
            const eventsWon = Number(valuesByKey.eventsWon ?? 0)
            const awardsWon = Number(valuesByKey.awardsWon ?? 0)
            const dcmpSlots = Number(valuesByKey.dcmpSlots ?? 0)
            // const cmpSlots = Number(valuesByKey.cmpSlots ?? 0)

            return (
                <div id="custom-tooltip" style={{width: 150, pointerEvents: 'auto', animation: 'none', position: 'relative', left: -120}}>
                    <p style={{marginBottom: 20}}>{label}</p>
                    <table style={{marginBottom: 30}}>
                        <tr>
                            <td><strong>Median</strong></td>
                            <td><strong>{median.toFixed(2)}</strong></td>
                        </tr>
                        <tr>
                            <td>Mean</td>
                            <td>{mean.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>Minimum</td>
                            <td>{min.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>1st Quartile</td>
                            <td>{lowerQuartile.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>3rd Quartile</td>
                            <td>{upperQuartile.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>Maximum</td>
                            <td>{max.toFixed(2)}</td>
                        </tr>
                    </table>
                    <table style={{marginBottom: 30}}>
                        <tr>
                            <td>Teams Included</td>
                            <td>{numTeams}</td>
                        </tr>
                        <tr>
                            <td>Returning Veterans</td>
                            <td>{returningVeterans}</td>
                        </tr>
                        <tr>
                            <td>Restarted Veterans</td>
                            <td>{restartedVeterans}</td>
                        </tr>
                        <tr>
                            <td>Rookie Teams</td>
                            <td>{rookieTeams}</td>
                        </tr>
                        <tr>
                            <td>Folded Teams</td>
                            <td>{foldedTeams}</td>
                        </tr>
                        <tr>
                            <td>Events Won</td>
                            <td>{eventsWon}</td>
                        </tr>
                        <tr>
                            <td>Awards Won</td>
                            <td>{awardsWon}</td>
                        </tr>
                        <tr>
                            <td>DCMP Slots Earned</td>
                            <td>{dcmpSlots}</td>
                        </tr> 
                    </table>
                    {label == 2020 || label == 2021 ? <p>Teams that did not play in 2020 but were registered for cancelled events are counted as folded teams in 2020, and as restarted veterans in 2022.</p> : null}
                    {label == 2020 || label == 2021 ? <p>This may change in a future release.</p> : null}
                </div>
            );
        }
        
        return null;
    };

    let ActiveDot = (props: any) => {
        const { cx, cy, type, payload } = props;
        if (payload?.numTeams === 0 || cx == null || cy == null) {
            return null;
        }

        return (
            <circle cx={cx} cy={cy} r={type.includes("median") ? 8 : 6} stroke={tealColorClear} strokeWidth={2} fill="transparent" />
        );
    };

    let TeamDot = (props: any) => {
        const { cx, cy, dataKey, value, payload } = props;
        const id = payload["Year"] + "" + dataKey

        if (cy == null) return <></>

        // toggle text visibility on clicked
        return (
            <>
                <circle className="teamDot" visibility={"hidden"} cx={cx} cy={cy} r={3} fill={offWhiteColor} onClick={() => toggleElementVisibility(dataKey + "line")} onMouseOver={() => toggleElementVisibility(id)}/>
                <text className="teamText" id={id} visibility={"hidden"} x={cx + 12} y={cy + 3}><a style={{fontFamily: "Arial, Helvetica, sans-serif", fontSize: 10, stroke: offWhiteColor, strokeWidth: 0.01, fill: offWhiteColor, textDecoration: "underline"}} target="_blank" href={'https://www.thebluealliance.com/team/' + dataKey + '/' + payload["Year"]}>{dataKey}: {value.toFixed(2)}</a></text>
            </>
        );
    };

    let toggleElementVisibility = (id: string) => {
        if (document.getElementById(id)?.style.visibility == "visible") {
            individualTeamsVisible = [...individualTeamsVisible].filter((value) => value != id)
            document.getElementById(id)!.style.visibility = 'hidden' 
        }
        else {
            document.getElementById(id)!.style.visibility = 'visible'
            individualTeamsVisible = [...individualTeamsVisible, id]
        }
        console.log(individualTeamsVisible)
    }

    const CustomDot = (props: any) => {
        const { cx, cy, type, payload } = props;
        if (payload?.numTeams === 0 || cx == null || cy == null) {
            return null;
        }

        return (
            <circle cx={cx} cy={cy} r={type.includes("median") ? 5 : 4} fill={tealColorClear} />
        );
    };

    const getIndividualTeamDisplays = () => {
        const teams = props.teams!.map((object) => object.team)
        const teamDisplays = []
        for (const team in teams) {
            const id = teams[team as any] + "line"
            console.log(id)
            teamDisplays.push(<Line type="monotone" id={id} dataKey={teams[team as any]} stroke="#EEEEEE" style={{visibility: "hidden"}} dot={<TeamDot/>} activeDot={<TeamDot/>}/>)
        }
        return teamDisplays
    }

    let dataVerificationOutput : ReactElement[] = []

    if (graphInvalidData.length > 0) {
        if (graphInvalidData.length == 1) {
            dataVerificationOutput.push(<li>Input "{graphInvalidData[0]}" is not a valid district team</li>)
        }
        else {
            dataVerificationOutput.push(<li>Inputs {graphInvalidData.map((team, index) => (index == graphInvalidData.length-1 && graphInvalidData.length != 1 ? "and " : "") + '"' + team + '" ')} are not valid district teams</li>)
        }
    }

    if (props.startYear as number < 1997 || props.startYear as number > 2026 || Number.isNaN(props.startYear as number)) {
        dataVerificationOutput.push(<li>Start year must be a number between 1997 and 2026</li>)
    }
    
    if (props.endYear as number < 1997 || props.endYear as number > 2026 || Number.isNaN(props.endYear as number)) {
        dataVerificationOutput.push(<li>End year must be a number between 1997 and 2026</li>)
    }

    if ((props.startYear as number) > (props.endYear as number)) {
        dataVerificationOutput.push(<li>End year must be greater than start year</li>)
    }

    if (dataVerificationOutput.length > 1) {
        return <div><p>Input errors: </p><ul>{dataVerificationOutput.map(element => element)}</ul></div>
    }
    else if (dataVerificationOutput.length == 1) {
        return <div><p>Input error: </p><ul>{dataVerificationOutput.map(element => element)}</ul></div>
    }

    return show ? <CircularProgress id="loading-icon"/>: <div style={{width: '60vw', height: '80vh'}}>
        <div style={{display: 'flex', justifyContent: 'center'}}>
                <div style={{display: 'flex', alignItems: "center", marginRight: 30}}>
                    <p style={{fontSize: 12, marginRight: 10}}>Median Percentile Rank</p>
                    <div className="line"></div>
                </div>
                <div style={{display: 'flex', alignItems: "center"}}>
                    <p style={{fontSize: 12, marginRight: 10}}>Mean Percentile Rank</p>
                    <div className="line" id="dashed-line"></div>
                </div>
        </div>
        <ResponsiveContainer width={'100%'} height={'90%'}>
        <ComposedChart data={newData}>
            <XAxis fontFamily="Arial, Helvetica, sans-serif" fontSize={11} strokeWidth={3} stroke="#EEEEEE" dataKey="Year" tickLine={false} />
            <CartesianGrid opacity={"15%"} stroke="#EEEEEE" />
            <YAxis domain={[0, 100]} allowDataOverflow={true} fontFamily="Arial, Helvetica, sans-serif" fontSize={11} strokeWidth={3} stroke="#EEEEEE" tickLine={false}/>
            <Tooltip trigger="click" position={{ x: 0, y: 0 }} content={<CustomTooltip/>} contentStyle={{backgroundColor: "#FF000000", border: "none"}} labelStyle={{fontSize: 14}} itemStyle={{fontSize: 14, fontFamily: "Arial, Helvetica, sans-serif", color: "#EEEEEE", lineHeight: 0.5}} />
            <Tooltip trigger="click" position={{ x: 10, y: 10 }} content={<CustomTooltip/>} contentStyle={{backgroundColor: "#FF000000", border: "none"}} labelStyle={{fontSize: 14}} itemStyle={{fontSize: 14, fontFamily: "Arial, Helvetica, sans-serif", color: "#EEEEEE", lineHeight: 0.5}} />
            <Line type="monotone" dataKey="mean" stroke={tealColorClear} strokeWidth={2} strokeDasharray='4, 2' activeDot={<ActiveDot type="mean"/>} dot={<CustomDot type="mean"/>}/>
            <Line type="monotone" dataKey="median" stroke={tealColor} strokeWidth={2} activeDot={<ActiveDot type="median"/>} dot={<CustomDot type="median"/>}/>
            <Bar stackId={'a'} dataKey={'min'} fill={'none'} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'bar'} shape={<HorizonBar />} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'bottomWhisker'} shape={<DotBar />} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'bottomBox'} shape={<BarOutline/>} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'topBox'} shape={<BarOutline/>} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'topWhisker'} shape={<DotBar />} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'bar'} shape={<HorizonBar />} legendType="none" activeBar={false}/>
            <Bar stackId={'a'} dataKey={'numTeams'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'returningVeterans'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'restartedVeterans'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'rookieTeams'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'foldedTeams'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'eventsWon'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'awardsWon'} fill={'none'}/>
            <Bar stackId={'a'} dataKey={'dcmpSlots'} fill={'none'}/>
            {getIndividualTeamDisplays()}
        <ZAxis type='number' dataKey='size' range={[0, 250]} />
        </ComposedChart>
        </ResponsiveContainer>
        <p style={{textAlign: "center", marginBottom: 0}}>Number of Input Teams: {
            // Counts number of unique teams in input
            props.teams!.map(object => object.team).filter((value, index, self) => self.indexOf(value) === index).length
        }</p>
        <div style={{display: 'flex', justifyContent: 'center'}}>
            <p style={{fontSize: 14, marginLeft: 10}}>Display individual teams</p>
            <Checkbox
                onChange={() => {
                    const dots = document.getElementsByClassName("teamDot")!
                    const teamsText = document.getElementsByClassName("teamText")!
                    individualTeamsToggle = !individualTeamsToggle;
                    for (const index in dots) {
                        (dots.item(Number(index))! as HTMLElement).style.visibility = individualTeamsToggle ? "visible" : "hidden"
                    }
                    // Hide all team numbers
                    if (individualTeamsToggle == false) {
                        for (const index in teamsText) {
                            (teamsText.item(Number(index))! as HTMLElement).style.visibility = "hidden"
                        }
                    }
                    // Show any team numbers that were previously toggled on
                    if (individualTeamsToggle) {
                        for (const index in individualTeamsVisible) {
                            document.getElementById(individualTeamsVisible[index])!.style.visibility = "visible"
                        }
                    }
                }}
                sx={{
                    color: tealColor,
                    '&.Mui-checked': {
                      color: tealColor,
                    },
                  }}></Checkbox>
        </div>
    </div>
}

export default GraphOutputComponent